import React, { useEffect, useState } from "react";
import Divider from "../ui/Divider";
import Space from "../ui/Space";
import Nav from "../ui/Nav";
import IconNav from "../ui/IconNav";
import Button from "../ui/Button";
import TextButton from "../ui/TextButton";
import ModalButton from "../ui/ModalButton";
import Card from "../ui/Card";
import Label, { DisplayLabel } from "../ui/Label";
import Input from "../ui/Input";
import PriceField from "../ui/PriceField";
import DateInput from "../ui/DateInput";
import TimeInput from "../ui/TimeInput";
import EnumField from "../ui/EnumField";
import SelectBox from "../ui/SelectBox";
import TextArea from "../ui/TextArea";
import Checkbox from "../ui/Checkbox";
import Segmented from "../ui/Segmented";
import Tags from "../ui/Tags";
import { Tag } from "../ui/Tag";
import Status from "../ui/Status";
import Alert from "../ui/Alert";
import Spin from "../ui/Spin";
import ToolTip from "../ui/ToolTip";
import Tabs from "../ui/Tabs";
import Menu from "../ui/Menu";
import VirtualTable from "../ui/VirtualTable";
import Grid from "../ui/Grid";
import Float from "../ui/Float";
import { showError, showMessage } from "../ui/Message";
import { listen } from "../hooks/Listener";
import { KBD } from "../utils/Keybindings";
import { ROUTES } from "../utils/Routes";
import { kbdEvent, showConfirm, showInfo } from "../utils/Utils";
import { CopyIcon, DeleteIcon, EditIcon, InfoIcon, PlusIcon, PrintIcon, SearchIcon, SettingsIcon } from "../utils/Icons";

// Hidden UI review screen: every UI kit element in one scrollable view, for checking light and dark themes.
// Not in the menus. Opened with [U] on the Home menu (non-beta only).

const TOKENS = [
    "--site-background", "--console-bg", "--surface-app", "--surface-container", "--surface-control", "--surface-hover",
    "--surface-rail", "--surface-raised", "--card-bg", "--display-bg", "--input-bg", "--input-edit-bg", "--row-bg",
    "--selection-row", "--total-fill", "--tile-attention", "--tile-go", "--rail-active-fill", "--rail-icon", "--rail-icon-active",
    "--text", "--text-primary", "--text-strong", "--text-light", "--text-dim", "--text-muted", "--text-disabled", "--label",
    "--text-link", "--text-attention", "--text-on-fill", "--text-on-selection", "--text-black",
    "--text-red", "--text-light-red", "--text-orange", "--text-orange-hover", "--text-peach", "--text-yellow", "--text-olive",
    "--text-green", "--text-bright-green", "--text-teal", "--text-blue", "--text-dark-blue", "--text-purple", "--text-bright-purple", "--text-pink",
    "--border-primary", "--border-secondary", "--border-strong", "--border-subtle", "--focus-ring", "--icon-default",
    "--action-link", "--action-primary", "--save-btn", "--cancel-btn",
    "--bg-black", "--bg-dark-blue", "--bg-dark-green", "--bg-dark-orange", "--bg-dark-red",
    "--status-new", "--status-pending", "--status-picking", "--status-ready", "--status-waiting", "--status-hold",
    "--status-for-later", "--status-notify", "--status-problem",
];

const TOKEN_GROUPS = [
    { title: "Surfaces & backgrounds", match: (t) => !t.startsWith("--text") && !t.startsWith("--border") && !t.startsWith("--status") && !t.startsWith("--bg-") && !["--focus-ring", "--icon-default", "--action-link", "--action-primary", "--save-btn", "--cancel-btn", "--label"].includes(t) },
    { title: "Text", match: (t) => t.startsWith("--text") || t === "--label" },
    { title: "Borders, focus & actions", match: (t) => t.startsWith("--border") || ["--focus-ring", "--icon-default", "--action-link", "--action-primary", "--save-btn", "--cancel-btn"].includes(t) },
    { title: "Dark fills", match: (t) => t.startsWith("--bg-") },
    { title: "Order status", match: (t) => t.startsWith("--status") },
];

const TEXT_CLASSES = ["red", "orange", "peach", "yellow", "olive", "green", "teal", "blue", "purple", "bright-purple", "pink", "light", "black"];
const TEXT_BG_CLASSES = ["red", "orange", "yellow", "olive", "green", "teal", "blue", "purple", "light", "black"];

const SAMPLE_ROWS = [
    { id: 1, isbn: "9780143127741", title: "The Overstory", author: "Powers, Richard", onhand: 4, price: 2400 },
    { id: 2, isbn: "9780385547345", title: "Lessons in Chemistry", author: "Garmus, Bonnie", onhand: 0, price: 3600 },
    { id: 3, isbn: "9780735219090", title: "Where the Crawdads Sing", author: "Owens, Delia", onhand: 12, price: 2200 },
    { id: 4, isbn: "9780593321201", title: "Tomorrow, and Tomorrow, and Tomorrow", author: "Zevin, Gabrielle", onhand: 2, price: 3800 },
    { id: 5, isbn: "9781250301697", title: "The Silent Patient", author: "Michaelides, Alex", onhand: 7, price: 2299 },
    { id: 6, isbn: "9780525559474", title: "The Midnight Library", author: "Haig, Matt", onhand: 1, price: 3500 },
    { id: 7, isbn: "9780063021426", title: "Babel", author: "Kuang, R. F.", onhand: 0, price: 3999 },
    { id: 8, isbn: "9780593135204", title: "Project Hail Mary", author: "Weir, Andy", onhand: 9, price: 3800 },
];

export default function StyleguideController(props) {
    const {
        controller = "styleguide",
        route = ROUTES.MISC.STYLEGUIDE,
        setController = () => { },
    } = props;

    const [theme, setTheme] = useState(() => document.documentElement.getAttribute("data-theme") || "dark");
    const [tokenValues, setTokenValues] = useState({});

    const [text, setText] = useState("Editable text");
    const [price, setPrice] = useState(2499);
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [enumValue, setEnumValue] = useState("T");
    const [selectValue, setSelectValue] = useState("6");
    const [notes, setNotes] = useState("Multi-line text area.\nSecond line.");
    const [checkA, setCheckA] = useState(true);
    const [checkB, setCheckB] = useState(false);
    const [segment, setSegment] = useState("all");
    const [tab, setTab] = useState("one");
    const [menuSelected, setMenuSelected] = useState("A");
    const [loading, setLoading] = useState(true);
    const [current, setCurrent] = useState(SAMPLE_ROWS[1]);

    // Track the theme set by MainNav and re-read token values whenever it changes
    useEffect(() => {
        const read = () => {
            const root = document.documentElement;
            const css = getComputedStyle(root);
            const _values = {};
            TOKENS.forEach(t => { _values[t] = css.getPropertyValue(t).trim(); });
            setTheme(root.getAttribute("data-theme") || "dark");
            setTokenValues(_values);
        };
        read();
        const observer = new MutationObserver(read);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
        return () => observer.disconnect();
    }, []);

    const toggleTheme = () => window.dispatchEvent(new CustomEvent("toggleTheme"));

    const nav = [
        { text: "Back", type: "back", key: KBD.ESCAPE, action: () => setController("home") },
        { text: "Theme", key: KBD.T, action: () => toggleTheme() },
    ];

    useEffect(() => listen(controller, route, nav), [theme]);

    const next = (_rows = 1) => {
        const i = SAMPLE_ROWS.findIndex(r => r.id === current?.id);
        setCurrent(SAMPLE_ROWS[Math.min(i + _rows, SAMPLE_ROWS.length - 1)]);
    };
    const prev = (_rows = 1) => {
        const i = SAMPLE_ROWS.findIndex(r => r.id === current?.id);
        setCurrent(SAMPLE_ROWS[Math.max(i - _rows, 0)]);
    };

    const table_columns = [
        { key: "isbn", title: "ISBN", align: "left", width: 120 },
        { key: "title", title: "Title", align: "left", width: 260, grow: true },
        { key: "author", title: "Author", align: "left", width: 160 },
        { key: "onhand", title: "OH", align: "right", width: 50, render: (e) => <span className={(e === 0) ? "text-red" : ""}>{e}</span> },
        { key: "price", title: "Price", align: "right", width: 70, render: (e) => "$" + (e / 100).toFixed(2) },
    ];

    const section = (title, children) => (
        <div style={{ "padding": "0px 15px" }}>
            <div className="shim" /><div className="shim" />
            <Divider align="left"><strong>{title}</strong></Divider>
            <div className="shim" />
            {children}
            <div className="shim" /><div className="shim" />
        </div>
    );

    const caption = (str) => <div style={{ "color": "var(--text-dim)", "fontSize": "11px", "paddingBottom": "4px" }}>{str}</div>;

    const sample = (label, el) => (
        <div style={{ "display": "inline-block", "verticalAlign": "top", "marginRight": "20px", "marginBottom": "12px" }}>
            {caption(label)}
            {el}
        </div>
    );

    const drawTokens = () => TOKEN_GROUPS.map(group => (
        <div key={group.title}>
            <div style={{ "fontSize": "12px", "padding": "6px 0px" }}><strong>{group.title}</strong></div>
            <div style={{ "display": "grid", "gridTemplateColumns": "repeat(auto-fill, minmax(200px, 1fr))", "gap": "6px" }}>
                {TOKENS.filter(group.match).map(t => (
                    <div key={t} style={{ "display": "flex", "alignItems": "center", "gap": "8px", "border": "1px solid var(--border-secondary)", "borderRadius": "4px", "padding": "4px" }}>
                        <div style={{ "width": "28px", "height": "28px", "flexShrink": 0, "borderRadius": "3px", "border": "1px solid var(--border-primary)", "backgroundColor": `var(${t})` }} />
                        <div style={{ "minWidth": 0, "lineHeight": "14px" }}>
                            <div style={{ "fontSize": "11px", "fontFamily": "var(--font-mono)" }}>{t}</div>
                            <div style={{ "fontSize": "10px", "color": "var(--text-dim)", "fontFamily": "var(--font-mono)", "whiteSpace": "nowrap", "overflow": "hidden", "textOverflow": "ellipsis" }}>{tokenValues[t] || "(not set)"}</div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="shim" />
        </div>
    ));

    return (
        <div className="controller controller-layout">
            <div className="header-card">
                <Float>
                    <div style={{ "padding": "8px 10px" }}>
                        <strong>UI Styleguide</strong>
                        <span style={{ "color": "var(--text-dim)" }}>&nbsp;&nbsp;Current theme: <strong>{theme}</strong></span>
                    </div>
                    <div style={{ "padding": "0px 10px" }}>
                        <Button type="primary" kbd={KBD.T} onClick={() => kbdEvent(KBD.T)}>Switch to {(theme === "dark") ? "light" : "dark"} &nbsp;</Button>
                    </div>
                </Float>
            </div>

            <div className="flex-height" style={{ "overflowY": "auto", "minHeight": 0 }}>

                {section("Theme tokens (vars.css)", drawTokens())}

                {section("Typography & text classes", <>
                    <div>Default body text. <strong>Strong</strong>, <em>em</em>, <small>small</small>, <span className="bold">.bold</span>, <span style={{ "fontFamily": "var(--font-mono)" }}>var(--font-mono) 9780143127741</span></div>
                    <div className="shim" />
                    <Space size={12} style={{ "flexWrap": "wrap" }}>
                        {TEXT_CLASSES.map(c => <span key={c} className={"text-" + c}>.text-{c}</span>)}
                    </Space>
                    <div className="shim" />
                    <Space size={8} style={{ "flexWrap": "wrap" }}>
                        {TEXT_BG_CLASSES.map(c => <span key={c} className={"text-" + c + "-bg"}>.text-{c}-bg</span>)}
                        <span className="text-bg">.text-bg</span>
                    </Space>
                    <div className="shim" />
                    <Space size={12} style={{ "flexWrap": "wrap" }}>
                        <span className="text-fade">.text-fade</span>
                        <span className="text-error">.text-error</span>
                        <span className="dim">.dim</span>
                        <span className="beta">.beta (disabled menu item)</span>
                        <span style={{ "color": "var(--text-link)" }}>var(--text-link)</span>
                        <span style={{ "color": "var(--text-muted)" }}>var(--text-muted)</span>
                        <span style={{ "color": "var(--text-disabled)" }}>var(--text-disabled)</span>
                    </Space>
                </>)}

                {section("Buttons", <>
                    {sample("Button", <Button>Default</Button>)}
                    {sample("type=primary", <Button type="primary">Primary</Button>)}
                    {sample("type=secondary", <Button type="secondary">Secondary</Button>)}
                    {sample("type=danger", <Button type="danger">Danger</Button>)}
                    {sample("type=link", <Button type="link">Link</Button>)}
                    {sample("type=small", <Button type="small">Small</Button>)}
                    {sample("styled", <Button styled>Styled</Button>)}
                    {sample("disabled", <Button disabled>Disabled</Button>)}
                    {sample("with kbd", <Button kbd={KBD.ENTER}>Save &nbsp;</Button>)}
                    {sample("TextButton", <TextButton onClick={() => showMessage("TextButton clicked")}>Text button</TextButton>)}
                    {sample("ModalButton", <ModalButton onClick={() => showMessage("ModalButton clicked")} value="Modal button" />)}
                </>)}

                {section("Inputs", <>
                    {sample("Input (display)", <Input value="Display value" width={160} />)}
                    {sample("Input edit", <Input edit controlled value={text} onChange={setText} width={160} />)}
                    {sample("Input edit placeholder", <Input edit placeholder="Placeholder..." width={160} />)}
                    {sample("Input secondary", <Input secondary value="Secondary" width={140} />)}
                    {sample("Input warning", <Input warning value="Warning" width={140} />)}
                    {sample("Input medium", <Input medium value="Medium" width={140} />)}
                    {sample("Input right", <Input right value="123.45" width={100} />)}
                    {sample("Input dim", <Input className="dim" value="Dimmed" width={120} />)}
                    <br />
                    {sample("PriceField (display)", <PriceField value={price} width={80} right />)}
                    {sample("PriceField edit", <PriceField edit value={price} onChange={setPrice} width={80} right />)}
                    {sample("DateInput edit", <DateInput edit value={date} onChange={setDate} width={95} />)}
                    {sample("TimeInput edit", <TimeInput edit value={time} onChange={setTime} width={95} />)}
                    {sample("EnumField (display)", <EnumField value={enumValue} options={[{ label: "Title", value: "T", key: KBD.T }, { label: "ISBN", value: "I", key: KBD.I }]} width={120} />)}
                    {sample("EnumField edit", <EnumField edit allow_empty={false} value={enumValue} onChange={setEnumValue} options={[{ label: "Title", value: "T", key: KBD.T }, { label: "ISBN", value: "I", key: KBD.I }]} width={120} />)}
                    {sample("SelectBox edit", <SelectBox edit width={140} value={selectValue} onChange={setSelectValue} options={[{ value: "6", label: "Last 6 Months" }, { value: "12", label: "Last 12 Months" }]} />)}
                    <br />
                    {sample("TextArea (display)", <TextArea value="Read-only text area" width={220} />)}
                    {sample("TextArea edit", <TextArea edit controlled value={notes} onChange={setNotes} width={220} height={60} />)}
                    {sample("Checkbox", <Space size={12}>
                        <Checkbox value={checkA} onChange={setCheckA}>Checked</Checkbox>
                        <Checkbox value={checkB} onChange={setCheckB}>Unchecked</Checkbox>
                        <Checkbox value={true} disabled>Disabled</Checkbox>
                    </Space>)}
                    {sample("Segmented", <Segmented value={segment} onChange={setSegment} options={[{ value: "all", label: "All" }, { value: "open", label: "Open", help: "Tooltip on a segment" }, { value: "closed", label: "Closed" }]} />)}
                    <br />
                    {sample("Tags (readvalue)", <Tags readvalue width={360} height={24} value="F|H" items={[{ label: "Fiction", value: "F" }, { label: "Hardcover", value: "H" }, { label: "Paperback", value: "P" }, { label: "Used", value: "U" }]} />)}
                    {sample("Tag states", <Space size={6}>
                        <Tag label="Tag" />
                        <Tag label="Active" active />
                        <Tag label="Current" current edit />
                        <Tag label="Current active" current active edit />
                    </Space>)}
                </>)}

                {section("Labels, cards & dividers", <>
                    {sample("Label", <Label>Label text</Label>)}
                    {sample("Label button", <Label button onClick={() => showMessage("Label clicked")}>Clickable label</Label>)}
                    {sample("DisplayLabel", <DisplayLabel>Hardcover</DisplayLabel>)}
                    {sample("Status", <Status type="active"><InfoIcon />[PgDn] to save, [Esc] to return</Status>)}
                    <div className="shim" />
                    <Space size={12} style={{ "alignItems": "flex-start" }}>
                        <Card header="Card" style={{ "width": "200px" }}>Primary card content.</Card>
                        <Card secondary header="Card secondary" style={{ "width": "200px" }}>Secondary card content.</Card>
                        <Card tertiary header="Card tertiary" style={{ "width": "200px" }}>Tertiary card content.</Card>
                        <Card style={{ "width": "200px" }}>Card without header.</Card>
                    </Space>
                    <div className="shim" /><div className="shim" />
                    <Divider />
                    <div className="shim" />
                    <Divider>Centered divider text</Divider>
                    <div className="shim" />
                    <Divider align="left">Left divider text</Divider>
                    <div className="shim" />
                    <Divider align="right">Right divider text</Divider>
                    <div className="shim" />
                    <Space style={{ "height": "20px", "alignItems": "center" }}><span>Vertical</span><Divider vertical /><span>divider</span></Space>
                </>)}

                {section("Feedback & dialogs", <>
                    <Space size={12} style={{ "alignItems": "flex-start", "flexWrap": "wrap" }}>
                        <div style={{ "width": "260px" }}><Alert title="Error alert" description="Default red alert." /></div>
                        <div style={{ "width": "260px" }}><Alert type="info" color="var(--text-blue)" title="Info alert" description="Blue alert with dismiss." dismissible /></div>
                        <div style={{ "width": "260px" }}><Alert type="warning" color="var(--text-orange)" description="Orange alert, no title." /></div>
                        <div style={{ "width": "260px" }}><Alert type="success" color="var(--text-green)" textColor="var(--text-on-fill)" title="Success" description="Green with text-on-fill." /></div>
                    </Space>
                    <div className="shim" /><div className="shim" />
                    <Space size={12} style={{ "alignItems": "flex-start" }}>
                        <div>
                            {caption("Spin")}
                            <Spin loading={loading}>
                                <Card style={{ "width": "200px", "height": "70px" }}>Content under a spinner.</Card>
                            </Spin>
                            <div className="shim" />
                            <Button type="small" onClick={() => setLoading(!loading)}>Toggle loading</Button>
                        </div>
                        <div>
                            {caption("Spin with message")}
                            <Spin loading={true} message="Loading...">
                                <Card style={{ "width": "200px", "height": "70px" }}>Content under a spinner.</Card>
                            </Spin>
                        </div>
                        <div>
                            {caption("ToolTip (hover)")}
                            <ToolTip text="Tooltip text">
                                <Label button>Hover me</Label>
                            </ToolTip>
                        </div>
                    </Space>
                    <div className="shim" /><div className="shim" />
                    {caption("Messages and modals (click to open)")}
                    <Space size={8} style={{ "flexWrap": "wrap" }}>
                        <Button onClick={() => showMessage("This is a message", 2500)}>showMessage</Button>
                        <Button onClick={() => showMessage("This is a success message", 2500, "success")}>showMessage success</Button>
                        <Button onClick={() => showMessage("Pinned message, click to close", 60000, "pinned")}>showMessage pinned</Button>
                        <Button onClick={() => showError({ error: "This is an error" })}>showError</Button>
                        <Button onClick={() => showConfirm({ controller, route, title: "Confirm", description: "Sample confirm dialog. Nothing happens either way." })}>showConfirm</Button>
                        <Button onClick={() => showInfo({ controller, route, title: "Info", description: "Sample info dialog." })}>showInfo</Button>
                    </Space>
                </>)}

                {section("Navigation", <>
                    {caption("Nav (bottom nav bar, with back/save, a separator, dimmed items and a right group)")}
                    <div style={{ "border": "1px solid var(--border-secondary)", "padding": "4px 0px" }}>
                        <Nav nav={[
                            { text: "Back", type: "back", action: () => showMessage("Back") },
                            { text: "Save", type: "save", action: () => showMessage("Save") },
                            { type: "shim", text: "" },
                            { text: "Edt", action: () => showMessage("Edt") },
                            { text: "@Edt", inds: [1], action: () => showMessage("@Edt") },
                            { text: "Cpy", action: () => showMessage("Cpy") },
                            { text: "Del", disabled: true },
                            { text: "Help", right: true, action: () => showMessage("Help") },
                        ]} />
                    </div>
                    <div className="shim" /><div className="shim" />
                    {caption("IconNav")}
                    <IconNav nav={[
                        { text: "Search", icon: <SearchIcon />, action: () => showMessage("Search") },
                        { text: "Add", icon: <PlusIcon />, action: () => showMessage("Add") },
                        { text: "Edit", icon: <EditIcon />, action: () => showMessage("Edit") },
                        { text: "Copy", icon: <CopyIcon />, action: () => showMessage("Copy") },
                        { type: "shim", text: "-" },
                        { text: "Delete", icon: <DeleteIcon />, disabled: true },
                        { text: "Print", icon: <PrintIcon />, right: true, action: () => showMessage("Print") },
                        { text: "Settings", icon: <SettingsIcon />, right: true, action: () => showMessage("Settings") },
                    ]} />
                    <div className="shim" /><div className="shim" />
                    <Space size={20} style={{ "alignItems": "flex-start" }}>
                        <div style={{ "width": "360px" }}>
                            {caption("Tabs")}
                            <Tabs selected={tab} onChange={setTab} height={60} tabData={[
                                { id: "one", label: "First", content: <div style={{ "padding": "8px" }}>First tab content</div> },
                                { id: "two", label: "Second", content: <div style={{ "padding": "8px" }}>Second tab content</div> },
                                { id: "three", label: "Third", content: <div style={{ "padding": "8px" }}>Third tab content</div> },
                            ]} />
                        </div>
                        <div style={{ "width": "360px" }}>
                            {caption("Tabs invert")}
                            <Tabs invert selected={tab} onChange={setTab} height={60} tabData={[
                                { id: "one", label: "First", content: <div style={{ "padding": "8px" }}>First tab content</div> },
                                { id: "two", label: "Second", content: <div style={{ "padding": "8px" }}>Second tab content</div> },
                                { id: "three", label: "Third", content: <div style={{ "padding": "8px" }}>Third tab content</div> },
                            ]} />
                        </div>
                        <div style={{ "width": "240px" }}>
                            {caption("Menu (click to select)")}
                            <Menu controller={controller} bordered keyName="option" selected={menuSelected} setSelected={setMenuSelected} menu={[
                                { text: "Add title", option: "A" },
                                { text: "Change location", option: "L" },
                                { text: "Print labels", option: "P" },
                                { text: "Cancel", option: "Esc" },
                            ]} />
                        </div>
                    </Space>
                </>)}

                {section("Tables", <>
                    {caption("VirtualTable (click a row, or use Up/Down)")}
                    <VirtualTable
                        controller={controller}
                        route={route}
                        header={<div>VirtualTable header</div>}
                        row_key="id"
                        data={SAMPLE_ROWS}
                        columns={table_columns}
                        current={current}
                        next={next}
                        prev={prev}
                        offset={0}
                        height={260}
                        onRowClick={(item) => setCurrent(item)}
                    />
                    <div className="shim" /><div className="shim" />
                    {caption("Grid")}
                    <Grid header="Grid header" width={500} height={0} row_key="id" data={SAMPLE_ROWS.slice(0, 4)} columns={[
                        { key: "title", title: "Title", align: "left", width: 280 },
                        { key: "onhand", title: "OH", align: "right", width: 60 },
                        { key: "price", title: "Price", align: "right", width: 80, render: (e) => "$" + (e / 100).toFixed(2) },
                    ]} />
                    <div className="shim" /><div className="shim" /><div className="shim" />
                </>)}

            </div>

            <div className="flex-bottom">
                <div className="shim" />
                <Divider />
                <div className="nav-wrapper">
                    <Space style={{ padding: "0px 15px" }}>
                        <Nav nav={nav} />
                    </Space>
                </div>
            </div>
        </div>
    );
}
